/**
 * app/components/Stats.tsx
 * -------------------------
 * What: Lightweight stats widget summarising key counts/metrics for the user.
 * Why: Gives a quick overview on the Home screen.
 */
// Stats.tsx
//
// This file defines the Stats component, which displays a summary of the user's inventory statistics.
// It shows the total number of items, rooms, and the total value of all stored items.
//
// Usage:
//   - Used on the home/dashboard screen to give users a quick overview of their inventory.
//   - Pulls data from DataContext (for total value) and SubscriptionContext (for item/room counts, currency).
//   - The component is styled and themed using ThemeContext.
//
// Features:
//   - Automatically refreshes the total value when mounted.
//   - Shows a loading state while stats are being fetched.
//   - Uses currency symbols based on the user's selected currency.
//
// Example:
//   <Stats />

import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useData } from '../../lib/DataContext';
import { useSubscription } from '../../lib/SubscriptionContext';
import { useTheme } from '../../lib/ThemeContext';

// Stats component: displays item/room counts and total value
export default function Stats() {
  // Get total value and loading state from DataContext
  const { totalValue, isLoading, refreshTotalValue } = useData();
  // Get item/room counts and currency from SubscriptionContext
  const { itemCount, roomCount, currency } = useSubscription();
  // Get current theme
  const { theme, themeType } = useTheme();

  // Build glow style for test theme
  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          })
    : {};

  // Currency symbols for display
  const currencySymbols = { GBP: '£', USD: '$', EUR: '€' };

  // The HomeScreen already triggers refreshAll() in DataContext, which in
  // turn fetches the latest total value. Calling refreshTotalValue here would
  // duplicate that query (and run twice due to function identity changes).
  // If Stats is ever mounted in a context where the data hasn’t been fetched
  // yet, consider invoking refreshTotalValue from that parent screen instead.

  // Show loading state if stats are being fetched
  if (isLoading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.card, borderColor: theme.border, borderWidth: 1 }]}>
        <Text style={[styles.loadingText, { color: theme.secondaryText }]}>Loading stats...</Text>
      </View>
    );
  }

  // Render stats summary
  return (
    <View style={[styles.container, { backgroundColor: theme.card, borderColor: theme.border, borderWidth: 1 }, glowStyle]}>
      {/* Header */}
      <View style={styles.headerRow}>
        <Text style={[styles.header, { color: theme.text }]}>My Stats</Text>
      </View>
      {/* Item count */}
      <View style={styles.row}>
        <Text style={[styles.label, { color: theme.secondaryText }]}>Items Stored</Text>
        <Text style={[styles.value, { color: theme.text }]}>{itemCount}</Text>
      </View>
      {/* Room count */}
      <View style={styles.row}>
        <Text style={[styles.label, { color: theme.secondaryText }]}>Rooms Configured</Text>
        <Text style={[styles.value, { color: theme.text }]}>{roomCount}</Text>
      </View>
      {/* Total value */}
      <View style={styles.row}>
        <Text style={[styles.label, { color: theme.secondaryText }]}>Total Value Stored</Text>
        <Text style={[styles.valueBold, { color: theme.text }]}>
          {currencySymbols[currency]}{typeof totalValue === 'number' && !isNaN(totalValue) ? totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
        </Text>
      </View>
    </View>
  );
}

// Styles for the stats card and text
const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  header: {
    fontSize: 17,
    fontWeight: '500',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 0,
    paddingVertical: 6,
  },
  label: {
    fontSize: 16,
  },
  value: {
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'right',
    minWidth: 60,
  },
  valueBold: {
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'right',
    minWidth: 100,
  },
  loadingText: {
    color: '#999',
    fontSize: 16,
    textAlign: 'center',
  },
}); 